import { Box, CircularProgress, Grid, makeStyles } from '@material-ui/core';
import React, { useEffect, useRef, useState } from 'react';
import DragAndDropComponent from 'src/components/DragAndDrop';
import { ReactComponent as UploadProfileIcon } from 'src/assets/images/upload-profile-image.svg';
import { FileDetails, StepStatus } from 'src/interfaces/onboarding.interface';
import { STEP_COMPLETED, STEP_EMPTY, STEP_LOADING } from 'src/components/StepsTracker';
import { useCreateHeadshot } from 'src/hooks/queries/useAccountSettings';
import { ONBOARDING_STEPS } from 'src/pages/MedspaAdmin/Onboarding/constants';
import { useUpdateMedspaAdminOnboardingProgress } from 'src/hooks/queries/useMedspaAdmins';

const useStyles = makeStyles({
  headshotContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '60px',
    height: '60px',
    position: 'relative',
  },
  profileImage: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    marginRight: '1rem',
    position: 'absolute',
    top: 0,
  },
});

interface HeadshotProps {
  stepId: number;
  onStepUpdate: (stepId: number, status: StepStatus) => void;
  headshot?: FileDetails;
  progressPercentage?: number;
  disabled?: boolean;
}

const Headshot = ({ stepId, onStepUpdate, headshot, progressPercentage, disabled = false }: HeadshotProps) => {
  const classes = useStyles();
  const prevState = useRef(headshot?.fileName);
  const [profilePreview, setProfilePreview] = useState<string>('');
  const [fileErrorMessage, setFileErrorMessage] = useState<string>('');

  const { mutateAsync: updateProgress } = useUpdateMedspaAdminOnboardingProgress();

  const {
    mutateAsync: createHeadshot,
    isSuccess,
    isError: createError,
    isLoading: isLoadingCreate,
  } = useCreateHeadshot();

  const handleFileInput = (fileData: File[]) => {
    const file = fileData?.[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onloadend = () => {
        createHeadshot({ headshot: reader.result as string });
      };
    } else {
      setFileErrorMessage('No file selected. Please select a file.');
    }
  };

  useEffect(() => {
    if (isLoadingCreate) {
      onStepUpdate(stepId, STEP_LOADING);
    } else if (createError) {
      onStepUpdate(stepId, STEP_EMPTY);
      setFileErrorMessage('Error updating headshot. Please try again.');
      setProfilePreview('');
    }
  }, [createError, isLoadingCreate]);

  useEffect(() => {
    setProfilePreview(headshot?.fileName ? headshot.url : '');
    onStepUpdate(stepId, headshot?.fileName ? STEP_COMPLETED : STEP_EMPTY);
  }, [headshot?.fileName, isSuccess]);

  useEffect(() => {
    if (prevState.current === null && headshot?.fileName) {
      updateProgress({
        stepName: ONBOARDING_STEPS.ACCOUNT_SETTINGS_NAME,
        percentage: progressPercentage as number,
      });
    }

    prevState.current = headshot?.fileName;
  }, [headshot?.fileName]);

  if (isLoadingCreate) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress size={50} style={{ marginRight: '10px' }} />
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Grid container>
      <Grid item xs={2}>
        <Box className={classes.headshotContainer}>
          {profilePreview ? (
            <img
              src={profilePreview}
              data-testid="headshot-profile-preview"
              alt="Profile preview"
              className={classes.profileImage}
            />
          ) : (
            <UploadProfileIcon data-testid="upload-profile-icon" className={classes.profileImage} />
          )}
        </Box>
      </Grid>

      <Grid item xs={10}>
        <DragAndDropComponent
          data-testid="drag-and-drop-headshot-input"
          hideDocumentIcon
          hideRemoveFile
          acceptedFileType="image/png,image/jpg,image/jpeg"
          onFileChange={handleFileInput}
          error={!!fileErrorMessage}
          height="80px"
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );
};

export default Headshot;
